import { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import authService from './services/authService';
import LandingPage from './pages/public/LandingPage';
import WaitlistPage from './pages/public/WaitlistPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import EmailVerificationPage from './pages/auth/EmailVerificationPage';
import ParticipantJoinPage from './pages/participant/ParticipantJoinPage';
import SessionLobbyPage from './pages/participant/SessionLobbyPage';
import SessionWelcomePage from './pages/participant/SessionWelcomePage';
import PreSessionDashboardPage from './pages/participant/PreSessionDashboardPage';
import PostSessionDashboardPage from './pages/participant/PostSessionDashboardPage';
import ParticipantSessionHub from './pages/participant/ParticipantSessionHub';
import ManagerPortalPage from './pages/public/ManagerPortalPage';
import ParticipantDashboard from './pages/participant/ParticipantDashboard';
import CertificatesPage from './pages/participant/CertificatesPage';
import CertificateViewerPage from './pages/participant/CertificateViewerPage';
import ActivityHistoryPage from './pages/participant/ActivityHistoryPage';
import ClientDashboard from './pages/client/ClientDashboard';
import TrainerDashboard from './pages/trainer/TrainerDashboard';
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard';
import SessionsPage from './pages/client/SessionsPage';
import CreateSessionPage from './pages/client/CreateSessionPage';
import EditSessionPage from './pages/client/EditSessionPage';
import TrainersPage from './pages/client/TrainersPage';
import AddTrainerPage from './pages/client/AddTrainerPage';
import EditTrainerPage from './pages/client/EditTrainerPage';
import TrainerDetailPage from './pages/client/TrainerDetailPage';
import TrainerSessionsPage from './pages/trainer/TrainerSessionsPage';
import TrainerSessionDetailPage from './pages/trainer/TrainerSessionDetailPage';
import SessionSetupPage from './pages/trainer/SessionSetupPage';
import SessionControlPage from './pages/trainer/SessionControlPage';
import TrainerAnalyticsPage from './pages/trainer/TrainerAnalyticsPage';
import TrainerSessionAnalyticsPage from './pages/trainer/TrainerSessionAnalyticsPage';
import TrainerSchedulePage from './pages/trainer/TrainerSchedulePage';
import WorkbooksPage from './pages/trainer/WorkbooksPage';
import ClientWorkbooksPage from './pages/client/WorkbooksPage';
// TODO: Re-enable when backend endpoint is built
// import TrainerFacilitationFeedbackPage from './pages/trainer/TrainerFacilitationFeedbackPage';
import TrainerGuidancePage from './pages/trainer/TrainerGuidancePage';
import TrainerBillingPage from './pages/trainer/TrainerBillingPage';
import TrainerCreateSessionPage from './pages/trainer/TrainerCreateSessionPage';
import TrainerSimulationsPage from './pages/trainer/TrainerSimulationsPage';
import SimulationIntakePage from './pages/trainer/SimulationIntakePage';
import SimulationBuilderPage from './pages/trainer/SimulationBuilderPage';
import SimulationRehearsePage from './pages/trainer/SimulationRehearsePage';
import SimulationPreviewPage from './pages/trainer/SimulationPreviewPage';
import SimulationConsolePage from './pages/trainer/SimulationConsolePage';
import SimulationProjectorPage from './pages/trainer/SimulationProjectorPage';
import SimulationParticipantPage from './pages/participant/SimulationParticipantPage';
import SimulationsHomePage from './pages/trainer/SimulationsHomePage';
import MicroSimLibraryPage from './pages/trainer/MicroSimLibraryPage';
import MicroSimDetailPage from './pages/trainer/MicroSimDetailPage';
import MicroSimPreviewPage from './pages/trainer/MicroSimPreviewPage';
import MicroSimLaunchConsolePage from './pages/trainer/MicroSimLaunchConsolePage';
import MicroSimPlayPage from './pages/participant/MicroSimPlayPage';
import AdminMicroSimsPage from './pages/admin/AdminMicroSimsPage';
import AdminMicroSimTopicPage from './pages/admin/AdminMicroSimTopicPage';
import AdminSessionsPage from './pages/admin/AdminSessionsPage';
import AdminSessionMonitorPage from './pages/admin/AdminSessionMonitorPage';
import ParticipantActiveSessionPage from './pages/participant/ParticipantActiveSessionPage';
import WorkbookViewerPage from './pages/participant/WorkbookViewerPage';
import NotesPage from './pages/participant/NotesPage';
import ChallengesPage from './pages/participant/ChallengesPage';
import ParticipantReportsPage from './pages/participant/ParticipantReportsPage';
import IndividualReportPage from './pages/participant/IndividualReportPage';
import SessionReportPage from './pages/common/SessionReportPage';
import ParticipantDetailsPage from './pages/common/ParticipantDetailsPage';
import OrganizationsPage from './pages/admin/OrganizationsPage';
import OrganizationDetailPage from './pages/admin/OrganizationDetailPage';
import AddOrganizationPage from './pages/admin/AddOrganizationPage';
import EditOrganizationPage from './pages/admin/EditOrganizationPage';
import UsersPage from './pages/admin/UsersPage';
import AddUserPage from './pages/admin/AddUserPage';
import UserDetailPage from './pages/admin/UserDetailPage';
import EditUserPage from './pages/admin/EditUserPage';
import PricingPage from './pages/client/PricingPage';
import BillingPage from './pages/client/BillingPage';
import SubscriptionPage from './pages/client/SubscriptionPage';
import CheckoutSuccessPage from './pages/client/CheckoutSuccessPage';
import ClientAnalyticsPage from './pages/client/ClientAnalyticsPage';
import SuperAdminAnalyticsPage from './pages/admin/SuperAdminAnalyticsPage';
import AdminBillingPage from './pages/admin/AdminBillingPage';
import ReportsPage from './pages/client/ReportsPage';
import GroupReportPage from './pages/client/GroupReportPage';
import TerminologyPage from './pages/client/TerminologyPage';
import ClientHelpPage from './pages/client/ClientHelpPage';
import SettingsPage from './pages/common/SettingsPage';
import { NotificationProvider } from './contexts/NotificationContext';
import { SessionProvider } from './contexts/SessionContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { PreferencesProvider } from './contexts/PreferencesContext';
import ToastNotification from './components/notifications/ToastNotification';
import ToastProvider from './components/providers/ToastProvider';
import ErrorBoundary from './components/error/ErrorBoundary';

// Role-based route guard — redirects unauthorized users to their dashboard or login
const ProtectedRoute = ({ allowedRoles, requireFeature, requireAnyFeature, children }) => {
  const user = authService.getCurrentUser();

  if (!authService.isAuthenticated() || !user) {
    return <Navigate to="/login" replace />;
  }

  /**
   * Per-user feature grant (User.features), checked for trainers only — super-admins
   * and clients are not gated, matching middleware/auth.requireFeature on the server.
   *
   * This stops a typed URL rendering a page whose every API call will 403. It is NOT
   * the access control: the routes are, and they check the database rather than this
   * cached copy of the user.
   */
  if (requireFeature && user.role === 'trainer' && !user.features?.[requireFeature]) {
    return <Navigate to="/trainer/dashboard" replace />;
  }
  // Pages reachable through either of several features (e.g. the Simulations choice
  // screen, for team simulations or micro simulations).
  if (requireAnyFeature && user.role === 'trainer' && !requireAnyFeature.some((f) => user.features?.[f])) {
    return <Navigate to="/trainer/dashboard" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to user's own dashboard based on their role
    const roleDashboards = {
      'super-admin': '/admin/dashboard',
      'client': '/client/dashboard',
      'trainer': '/trainer/dashboard',
      'participant': '/participant/dashboard',
    };
    return <Navigate to={roleDashboards[user.role] || '/login'} replace />;
  }

  return children;
};

/**
 * Main App Component
 * Routes and application structure
 */

function App() {
  // Fix #12: refresh the JWT every 10 hours for authenticated users so active
  // users (trainers running 8h sessions, clients logged in for weeks) never
  // hit natural expiry. Anonymous participants don't have a token — skipped.
  useEffect(() => {
    if (!authService.isAuthenticated()) return undefined;

    // Kick off an initial refresh so a fresh token is in place if the current
    // one is close to expiry when the app mounts.
    authService.refreshToken();

    const TEN_HOURS = 10 * 60 * 60 * 1000;
    const interval = setInterval(() => {
      if (authService.isAuthenticated()) authService.refreshToken();
    }, TEN_HOURS);

    return () => clearInterval(interval);
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <PreferencesProvider>
          <ToastProvider>
            <SessionProvider>
              <NotificationProvider>
                <ToastNotification />
              <div className="App">
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/waitlist" element={<WaitlistPage />} />

        {/* Authentication Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/register/client" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/verify-email/:token" element={<EmailVerificationPage />} />

        {/* Participant Routes - Dashboard pages require auth */}
        <Route path="/participant/dashboard" element={<ProtectedRoute allowedRoles={['participant', 'trainer', 'client', 'super-admin']}><ParticipantDashboard /></ProtectedRoute>} />
        <Route path="/participant/certificates" element={<ProtectedRoute allowedRoles={['participant', 'trainer', 'client', 'super-admin']}><CertificatesPage /></ProtectedRoute>} />
        <Route path="/participant/certificates/:certificateId" element={<ProtectedRoute allowedRoles={['participant', 'trainer', 'client', 'super-admin']}><CertificateViewerPage /></ProtectedRoute>} />
        <Route path="/participant/activity-history" element={<ProtectedRoute allowedRoles={['participant', 'trainer', 'client', 'super-admin']}><ActivityHistoryPage /></ProtectedRoute>} />
        <Route path="/join" element={<ParticipantJoinPage />} />
        <Route path="/join/:sessionCode" element={<ParticipantJoinPage />} />
        <Route path="/session/:sessionId/welcome" element={<SessionWelcomePage />} />
        <Route path="/session/:sessionId/lobby" element={<SessionLobbyPage />} />
        <Route path="/session/:sessionId/pre-session" element={<PreSessionDashboardPage />} />
        <Route path="/session/:sessionId/post-session" element={<PostSessionDashboardPage />} />
        <Route path="/session/:sessionId/hub" element={<ParticipantSessionHub />} />
        {/* Anonymous participants — must NOT be wrapped in ProtectedRoute/DashboardLayout */}
        <Route path="/session/:sessionId/simulation" element={<SimulationParticipantPage />} />
        {/* Paired micro simulation — public like the other /session pages; the API
            requires this session's signed participant token. */}
        <Route path="/session/:sessionId/micro-sim/:runId" element={<MicroSimPlayPage />} />
        <Route path="/manager" element={<ManagerPortalPage />} />
        <Route path="/manager/:managerCode" element={<ManagerPortalPage />} />
        <Route path="/session/:sessionId/active" element={<ParticipantActiveSessionPage />} />
        <Route path="/session/:sessionId/workbooks" element={<WorkbookViewerPage />} />
        <Route path="/session/:sessionId/launched-workbooks" element={<NotesPage />} />
        <Route path="/session/:sessionId/notes" element={<NotesPage />} />
        <Route path="/session/:sessionId/workspace" element={<NotesPage />} />
        <Route path="/session/:sessionId/challenges" element={<ChallengesPage />} />
        <Route path="/session/:sessionId/reports" element={<ParticipantReportsPage />} />
        <Route path="/session/:sessionId/individual-report" element={<IndividualReportPage />} />

        {/* Super Admin Routes */}
        <Route path="/admin/dashboard" element={<ProtectedRoute allowedRoles={['super-admin']}><SuperAdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/organizations" element={<ProtectedRoute allowedRoles={['super-admin']}><OrganizationsPage /></ProtectedRoute>} />
        <Route path="/admin/organizations/new" element={<ProtectedRoute allowedRoles={['super-admin']}><AddOrganizationPage /></ProtectedRoute>} />
        <Route path="/admin/organizations/:organizationId/edit" element={<ProtectedRoute allowedRoles={['super-admin']}><EditOrganizationPage /></ProtectedRoute>} />
        <Route path="/admin/organizations/:organizationId" element={<ProtectedRoute allowedRoles={['super-admin']}><OrganizationDetailPage /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute allowedRoles={['super-admin']}><UsersPage /></ProtectedRoute>} />
        <Route path="/admin/users/new" element={<ProtectedRoute allowedRoles={['super-admin']}><AddUserPage /></ProtectedRoute>} />
        <Route path="/admin/users/:userId" element={<ProtectedRoute allowedRoles={['super-admin']}><UserDetailPage /></ProtectedRoute>} />
        <Route path="/admin/users/:userId/edit" element={<ProtectedRoute allowedRoles={['super-admin']}><EditUserPage /></ProtectedRoute>} />
        <Route path="/admin/sessions" element={<ProtectedRoute allowedRoles={['super-admin']}><AdminSessionsPage /></ProtectedRoute>} />
        <Route path="/admin/sessions/:sessionId" element={<ProtectedRoute allowedRoles={['super-admin']}><SessionReportPage /></ProtectedRoute>} />
        <Route path="/admin/sessions/:sessionId/monitor" element={<ProtectedRoute allowedRoles={['super-admin']}><AdminSessionMonitorPage /></ProtectedRoute>} />
        <Route path="/admin/sessions/:sessionId/report" element={<ProtectedRoute allowedRoles={['super-admin']}><SessionReportPage /></ProtectedRoute>} />
        <Route path="/admin/sessions/:sessionId/group-report" element={<ProtectedRoute allowedRoles={['super-admin']}><GroupReportPage /></ProtectedRoute>} />
        <Route path="/admin/sessions/:sessionId/participants/:participantId" element={<ProtectedRoute allowedRoles={['super-admin']}><ParticipantDetailsPage /></ProtectedRoute>} />
        <Route path="/admin/analytics" element={<ProtectedRoute allowedRoles={['super-admin']}><SuperAdminAnalyticsPage /></ProtectedRoute>} />
        <Route path="/admin/billing" element={<ProtectedRoute allowedRoles={['super-admin']}><AdminBillingPage /></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute allowedRoles={['super-admin']}><SettingsPage /></ProtectedRoute>} />
        {/* Micro simulation catalogue manager: upload, versions, publish */}
        <Route path="/admin/micro-sims" element={<ProtectedRoute allowedRoles={['super-admin']}><AdminMicroSimsPage /></ProtectedRoute>} />
        <Route path="/admin/micro-sims/:slug" element={<ProtectedRoute allowedRoles={['super-admin']}><AdminMicroSimTopicPage /></ProtectedRoute>} />
        <Route path="/admin/micro-sims/:slug/preview/:runId" element={<ProtectedRoute allowedRoles={['super-admin']}><MicroSimPreviewPage /></ProtectedRoute>} />
        <Route path="/admin/*" element={<ProtectedRoute allowedRoles={['super-admin']}><ComingSoon title="Super Admin Section" /></ProtectedRoute>} />

        {/* Client Routes */}
        <Route path="/client/dashboard" element={<ProtectedRoute allowedRoles={['client']}><ClientDashboard /></ProtectedRoute>} />
        <Route path="/client/sessions" element={<ProtectedRoute allowedRoles={['client']}><SessionsPage /></ProtectedRoute>} />
        <Route path="/client/sessions/new" element={<ProtectedRoute allowedRoles={['client']}><CreateSessionPage /></ProtectedRoute>} />
        <Route path="/client/sessions/:sessionId" element={<ProtectedRoute allowedRoles={['client']}><SessionReportPage /></ProtectedRoute>} />
        <Route path="/client/sessions/:sessionId/edit" element={<ProtectedRoute allowedRoles={['client']}><EditSessionPage /></ProtectedRoute>} />
        <Route path="/client/sessions/:sessionId/monitor" element={<ProtectedRoute allowedRoles={['client']}><SessionReportPage /></ProtectedRoute>} />
        <Route path="/client/sessions/:sessionId/report" element={<ProtectedRoute allowedRoles={['client']}><SessionReportPage /></ProtectedRoute>} />
        <Route path="/client/sessions/:sessionId/group-report" element={<ProtectedRoute allowedRoles={['client']}><GroupReportPage /></ProtectedRoute>} />
        <Route path="/client/sessions/:sessionId/participants/:participantId" element={<ProtectedRoute allowedRoles={['client']}><ParticipantDetailsPage /></ProtectedRoute>} />
        <Route path="/client/trainers" element={<ProtectedRoute allowedRoles={['client']}><TrainersPage /></ProtectedRoute>} />
        <Route path="/client/trainers/add" element={<ProtectedRoute allowedRoles={['client']}><AddTrainerPage /></ProtectedRoute>} />
        <Route path="/client/trainers/:trainerId" element={<ProtectedRoute allowedRoles={['client']}><TrainerDetailPage /></ProtectedRoute>} />
        <Route path="/client/trainers/:trainerId/edit" element={<ProtectedRoute allowedRoles={['client']}><EditTrainerPage /></ProtectedRoute>} />
        {/* TODO: Re-enable when backend endpoint is built */}
        {/* <Route path="/client/trainers/:trainerId/feedback/:sessionId" element={<TrainerFacilitationFeedbackPage />} /> */}
        <Route path="/client/reports" element={<ProtectedRoute allowedRoles={['client']}><ReportsPage /></ProtectedRoute>} />
        <Route path="/client/pricing" element={<ProtectedRoute allowedRoles={['client']}><PricingPage /></ProtectedRoute>} />
        <Route path="/client/billing" element={<ProtectedRoute allowedRoles={['client']}><BillingPage /></ProtectedRoute>} />
        <Route path="/client/subscription" element={<ProtectedRoute allowedRoles={['client']}><SubscriptionPage /></ProtectedRoute>} />
        <Route path="/client/checkout/success" element={<ProtectedRoute allowedRoles={['client']}><CheckoutSuccessPage /></ProtectedRoute>} />
        <Route path="/client/analytics" element={<ProtectedRoute allowedRoles={['client']}><ClientAnalyticsPage /></ProtectedRoute>} />
        <Route path="/client/workbooks" element={<ProtectedRoute allowedRoles={['client']}><ClientWorkbooksPage /></ProtectedRoute>} />
        <Route path="/client/settings" element={<ProtectedRoute allowedRoles={['client']}><SettingsPage /></ProtectedRoute>} />
        <Route path="/client/terminology" element={<ProtectedRoute allowedRoles={['client']}><TerminologyPage /></ProtectedRoute>} />
        <Route path="/client/help" element={<ProtectedRoute allowedRoles={['client']}><ClientHelpPage /></ProtectedRoute>} />
        <Route path="/client/*" element={<ProtectedRoute allowedRoles={['client']}><ComingSoon title="Client Section" /></ProtectedRoute>} />

        {/* Trainer Routes */}
        <Route path="/trainer/dashboard" element={<ProtectedRoute allowedRoles={['trainer']}><TrainerDashboard /></ProtectedRoute>} />
        <Route path="/trainer/sessions" element={<ProtectedRoute allowedRoles={['trainer']}><TrainerSessionsPage /></ProtectedRoute>} />
        <Route path="/trainer/sessions/new" element={<ProtectedRoute allowedRoles={['trainer']}><TrainerCreateSessionPage /></ProtectedRoute>} />
        <Route path="/trainer/sessions/:sessionId" element={<ProtectedRoute allowedRoles={['trainer']}><TrainerSessionDetailPage /></ProtectedRoute>} />
        <Route path="/trainer/sessions/:sessionId/edit" element={<ProtectedRoute allowedRoles={['trainer']}><EditSessionPage /></ProtectedRoute>} />
        <Route path="/trainer/sessions/:sessionId/setup" element={<ProtectedRoute allowedRoles={['trainer']}><SessionSetupPage /></ProtectedRoute>} />
        <Route path="/trainer/session/:sessionId/control" element={<ProtectedRoute allowedRoles={['trainer']}><SessionControlPage /></ProtectedRoute>} />
        <Route path="/trainer/sessions/:sessionId/report" element={<ProtectedRoute allowedRoles={['trainer']}><SessionReportPage /></ProtectedRoute>} />
        <Route path="/trainer/sessions/:sessionId/group-report" element={<ProtectedRoute allowedRoles={['trainer']}><GroupReportPage /></ProtectedRoute>} />
        <Route path="/trainer/sessions/:sessionId/participants/:participantId" element={<ProtectedRoute allowedRoles={['trainer']}><ParticipantDetailsPage /></ProtectedRoute>} />
        {/* TODO: Re-enable when backend endpoint is built */}
        {/* <Route path="/trainer/sessions/:sessionId/facilitation-feedback" element={<TrainerFacilitationFeedbackPage />} /> */}
        <Route path="/trainer/schedule" element={<ProtectedRoute allowedRoles={['trainer']}><TrainerSchedulePage /></ProtectedRoute>} />
        <Route path="/trainer/workbooks" element={<ProtectedRoute allowedRoles={['trainer']}><WorkbooksPage /></ProtectedRoute>} />
        <Route path="/trainer/analytics" element={<ProtectedRoute allowedRoles={['trainer']}><TrainerSessionAnalyticsPage /></ProtectedRoute>} />
        <Route path="/trainer/performance" element={<ProtectedRoute allowedRoles={['trainer']}><TrainerAnalyticsPage /></ProtectedRoute>} />
        <Route path="/trainer/billing" element={<ProtectedRoute allowedRoles={['trainer']}><TrainerBillingPage /></ProtectedRoute>} />
        <Route path="/trainer/help" element={<ProtectedRoute allowedRoles={['trainer']}><TrainerGuidancePage /></ProtectedRoute>} />
        <Route path="/trainer/settings" element={<ProtectedRoute allowedRoles={['trainer']}><SettingsPage /></ProtectedRoute>} />

        {/* Simulations — must stay ABOVE the /trainer/* catch-all below */}
        {/* The Simulations tab opens a choice: team simulation or paired micro simulation. */}
        <Route path="/trainer/simulations" element={<ProtectedRoute allowedRoles={['trainer']} requireAnyFeature={['simulations', 'microSims']}><SimulationsHomePage /></ProtectedRoute>} />
        <Route path="/trainer/simulations/team" element={<ProtectedRoute allowedRoles={['trainer']} requireFeature="simulations"><TrainerSimulationsPage /></ProtectedRoute>} />
        <Route path="/trainer/micro-sims" element={<ProtectedRoute allowedRoles={['trainer']} requireFeature="microSims"><MicroSimLibraryPage /></ProtectedRoute>} />
        <Route path="/trainer/micro-sims/:slug" element={<ProtectedRoute allowedRoles={['trainer']} requireFeature="microSims"><MicroSimDetailPage /></ProtectedRoute>} />
        <Route path="/trainer/micro-sims/:slug/preview/:runId" element={<ProtectedRoute allowedRoles={['trainer']} requireFeature="microSims"><MicroSimPreviewPage /></ProtectedRoute>} />
        <Route path="/trainer/micro-sim-launches/:launchId" element={<ProtectedRoute allowedRoles={['trainer']} requireFeature="microSims"><MicroSimLaunchConsolePage /></ProtectedRoute>} />
        {/* "new" must precede the :simulationId param route, or it is read as an id */}
        <Route path="/trainer/simulations/new" element={<ProtectedRoute allowedRoles={['trainer']} requireFeature="simulations"><SimulationIntakePage /></ProtectedRoute>} />
        <Route path="/trainer/simulations/:simulationId" element={<ProtectedRoute allowedRoles={['trainer']} requireFeature="simulations"><SimulationBuilderPage /></ProtectedRoute>} />
        <Route path="/trainer/simulations/:simulationId/rehearse" element={<ProtectedRoute allowedRoles={['trainer']} requireFeature="simulations"><SimulationRehearsePage /></ProtectedRoute>} />
        <Route path="/trainer/simulations/:simulationId/preview" element={<ProtectedRoute allowedRoles={['trainer']} requireFeature="simulations"><SimulationPreviewPage /></ProtectedRoute>} />
        <Route path="/trainer/simulation-runs/:runId" element={<ProtectedRoute allowedRoles={['trainer']} requireFeature="simulations"><SimulationConsolePage /></ProtectedRoute>} />
        <Route path="/trainer/simulation-runs/:runId/projector" element={<ProtectedRoute allowedRoles={['trainer']} requireFeature="simulations"><SimulationProjectorPage /></ProtectedRoute>} />

        <Route path="/trainer/*" element={<ProtectedRoute allowedRoles={['trainer']}><ComingSoon title="Trainer Section" /></ProtectedRoute>} />

        {/* 404 Page */}
        <Route path="*" element={<NotFound />} />
      </Routes>
              </div>
              </NotificationProvider>
            </SessionProvider>
          </ToastProvider>
        </PreferencesProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

// Coming Soon placeholder component
function ComingSoon({ title }) {
  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-primary-100 rounded-full mb-6">
          <svg
            className="w-10 h-10 text-primary-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
        </div>
        <h1 className="text-4xl font-bold text-neutral-900 mb-3">{title}</h1>
        <p className="text-lg text-neutral-600 mb-8">
          This page is under construction
        </p>
        <div className="bg-white rounded-xl border border-neutral-200 p-8 max-w-md mx-auto shadow-sm">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4">
            ✅ What's Already Built:
          </h3>
          <ul className="text-left text-sm text-neutral-700 space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-0.5">✓</span>
              <span>Professional UI Components (Button, Card, Input, Badge, Modal, Avatar)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-0.5">✓</span>
              <span>Login Page with Mock Data Authentication</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-0.5">✓</span>
              <span>Complete Design System & Theme</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-0.5">✓</span>
              <span>Comprehensive Mock Data</span>
            </li>
          </ul>
          <div className="mt-6 pt-6 border-t border-neutral-200">
            <a
              href="/login"
              className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back to Login
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// 404 Not Found component
function NotFound() {
  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
      <div className="text-center">
        <h1 className="text-9xl font-bold text-neutral-300 mb-4">404</h1>
        <h2 className="text-3xl font-bold text-neutral-900 mb-3">Page Not Found</h2>
        <p className="text-neutral-600 mb-8">
          The page you're looking for doesn't exist.
        </p>
        <a
          href="/login"
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
            />
          </svg>
          Go to Login
        </a>
      </div>
    </div>
  );
}

export default App;
